import { inject, Injectable } from '@angular/core';
import {
  FetchCapacityReportArgs,
  GetCapacityReportGQL,
  GetCapacityReportsCountGQL,
  GetCapacityReportsGQL,
  GetFirstWeekReportIdGQL,
  SubmitAndCompleteCapacityReportGQL,
  UpdateCapacityReportGQL,
  UpdateCapacityReportInput,
} from '@libs/services/graphql/generated/ala-backend.service';
import { catchError, firstValueFrom, map, of } from 'rxjs';
import { CapacityReportRawType } from '../types-v2/raws/capacity-report-raw.type';
@Injectable({ providedIn: 'root' })
export class CapacityService {
  private getCapacityReportGQL = inject(GetCapacityReportGQL);
  private getCapacityReportsGQL = inject(GetCapacityReportsGQL);
  private getFirstWeekReportIdGQL = inject(GetFirstWeekReportIdGQL);
  private getCapacityReportsCountGQL = inject(GetCapacityReportsCountGQL);
  private updateCapacityReportGQL = inject(UpdateCapacityReportGQL);
  private submitAndCompleteCapacityReportGQL = inject(
    SubmitAndCompleteCapacityReportGQL
  );

  /**
   * Fetch capacity report by id
   */
  async fetchCapacityReport(id: string): Promise<CapacityReportRawType | null> {
    if (!id) return null;

    const result = await firstValueFrom(
      this.getCapacityReportGQL.fetch({ id }).pipe(
        map((res) => res.data?.capacityReport),
        catchError((err) => {
          console.error('fetchCapacityReport:', err);
          return of(null);
        })
      )
    );
    return (result as CapacityReportRawType) ?? null;
  }

  /**
   * Fetch capacity reports by filter
   */
  async fetchCapacityReportsCount(
    filter: FetchCapacityReportArgs
  ): Promise<number> {
    const result = await firstValueFrom(
      this.getCapacityReportsCountGQL
        .fetch({ fetchCapacityReportArgs: filter }, { fetchPolicy: 'no-cache' })
        .pipe(
          map((res) => res.data?.count),
          catchError((err) => {
            console.error('fetchCapacityReports:', err);
            return of(null);
          })
        )
    );

    return (result as number) ?? null;
  }

  /**
   * Fetch capacity reports by filter
   */
  async fetchCapacityReportFirstWeekId(id: string): Promise<string> {
    const result = await firstValueFrom(
      this.getFirstWeekReportIdGQL
        .fetch({ id }, { fetchPolicy: 'no-cache' })
        .pipe(
          map((res) => res.data?.getFirstWeekReportId),
          catchError((err) => {
            console.error('fetchCapacityReportFirstWeekId:', err);
            return of(null);
          })
        )
    );

    return (result as string) ?? null;
  }

  /**
   * Fetch capacity reports by filter
   */
  async fetchCapacityReports(
    filter: FetchCapacityReportArgs
  ): Promise<CapacityReportRawType[]> {
    const result = await firstValueFrom(
      this.getCapacityReportsGQL
        .fetch({ fetchCapacityReportArgs: filter }, { fetchPolicy: 'no-cache' })
        .pipe(
          map((res) => res.data?.capacityReports ?? []),
          catchError((err) => {
            console.error('fetchCapacityReports:', err);
            return of([]);
          })
        )
    );

    return (result as CapacityReportRawType[]) ?? null;
  }

  /**
   * Update capacity report by id and input fields
   */
  async updateCapacityReport(
    id: string,
    input: UpdateCapacityReportInput
  ): Promise<CapacityReportRawType | null> {
    const result = await firstValueFrom(
      this.updateCapacityReportGQL.mutate({ id, input }).pipe(
        map((res) => res.data?.updateCapacityReport),
        catchError((err) => {
          console.error('updateCapacityReport:', err);
          throw err;
        })
      )
    );

    return result ?? null;
  }

  /**
   * Submit and complete the report
   */
  async submitCapacityReport(
    id: string,
    facilityHasSmokingPolicy: boolean
  ): Promise<CapacityReportRawType | null> {
    const result = await firstValueFrom(
      this.submitAndCompleteCapacityReportGQL
        .mutate({ id, facilityHasSmokingPolicy })
        .pipe(
          map((res) => res.data?.submitAndCompleteCapacityReport),
          catchError((err) => {
            console.error('submitCapacityReport:', err);
            throw err;
          })
        )
    );
    return result ?? null;
  }
}
